import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  GroupingState,
  useReactTable,
} from "@tanstack/react-table";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Skeleton } from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import EmptyStateCard from "./empty-state-card.component";

const DEFAULT_TABLE_ITEMS_COUNT = 10;

interface TableComponentProps<T> {
  columns: ColumnDef<T, any>[];
  filters?: { [key: string]: any };
  className?: string;
  listMethod: (params: any) => Promise<[T[], number]>;
  getRowId: (row: T) => string;
  onRowClick?: (row: T) => void;
  loaderItemHeight?: number;
  refresh?: any;
  height?: number | string;
  hideHeader?: boolean;
  groupBy?: (keyof T)[];
  groupByDateKey?: keyof T;
  disableAutoGroupByDate?: boolean;
  formatGroupLabel?: (rawDate: string) => string;
}

export function TableComponent<T extends Record<string, any>>({
  columns,
  filters = {},
  listMethod,
  className,
  getRowId,
  onRowClick,
  loaderItemHeight = 104,
  refresh,
  height = "auto",
  hideHeader = false,
  groupBy = [],
  groupByDateKey,
  disableAutoGroupByDate = false,
  formatGroupLabel,
}: TableComponentProps<T>) {
  const observerRef = useRef<HTMLDivElement>(null);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [grouping, setGrouping] = useState<GroupingState>(groupBy as string[]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    refetch,
    error,
  } = useInfiniteQuery({
    queryKey: ["table", listMethod, filters],
    queryFn: async ({ pageParam = 0 }) => {
      const [rows, total] = await listMethod({
        ...filters,
        take: DEFAULT_TABLE_ITEMS_COUNT,
        skip: pageParam * DEFAULT_TABLE_ITEMS_COUNT,
      });

      return {
        rows,
        total,
        nextPage:
          rows.length === DEFAULT_TABLE_ITEMS_COUNT ? pageParam + 1 : undefined,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 0,
  });

  const items = useMemo(() => {
    if (disableAutoGroupByDate || !groupByDateKey)
      return data?.pages.flatMap((p) => p.rows) ?? [];

    const grouped: Record<string, T[]> = {};
    for (const row of data?.pages.flatMap((p) => p.rows) ?? []) {
      const raw = row[groupByDateKey];
      const date = raw ? new Date(raw).toISOString().split("T")[0] : "unknown";
      if (!grouped[date]) grouped[date] = [];
      grouped[date].push(row);
    }

    return Object.entries(grouped).flatMap(([date, rows]) => [
      {
        id: `group-${date}`,
        isGroup: true,
        groupLabel: formatGroupLabel ? formatGroupLabel(date) : date,
      } as any,
      ...rows,
    ]);
  }, [data, groupByDateKey, disableAutoGroupByDate, formatGroupLabel]);

  // const totalCount = useMemo(() => data?.pages[0]?.total ?? 0, [data]);

  const table = useReactTable({
    data: items,
    columns,
    state: {
      columnFilters,
      grouping,
    },
    onColumnFiltersChange: setColumnFilters,
    onGroupingChange: setGrouping,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getRowId,
  });

  // REFRESH
  useEffect(() => {
    if (refresh) refetch();
  }, [refresh]);

  // INFINITE SCROLL
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 1.0, rootMargin: "100px" },
    );
    if (observerRef.current) {
      observer.observe(observerRef.current);
    }
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (error) return <div>Error: {(error as Error).message}</div>;

  const NoItemsText = () => (
    <EmptyStateCard message="No items" Icon={BlockIcon} />
  );

  return (
    <div className={className} style={{ height, overflow: "auto" }}>
      {isLoading && (
        <>
          {[...Array(3)].map((_, i) => (
            <Skeleton
              key={i}
              variant="rounded"
              height={loaderItemHeight}
              style={{ marginBottom: 10 }}
            />
          ))}
        </>
      )}

      {!isLoading && !items.length && <NoItemsText />}

      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        {!hideHeader && (
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} style={{ textAlign: "left", padding: 8 }}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
        )}
        <tbody>
          {table.getRowModel().rows.map((row) => {
            const isGroupRow = row.original?.isGroup;
            if (isGroupRow) {
              return (
                <tr key={row.id}>
                  <td
                    colSpan={table.getAllLeafColumns().length}
                    style={{ padding: 2, fontWeight: 600 }}
                  >
                    {row.original.groupLabel}
                  </td>
                </tr>
              );
            }
            return (
              <tr key={row.id} onClick={() => onRowClick?.(row.original)}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {hasNextPage && (
        <div ref={observerRef} style={{ height: loaderItemHeight }}>
          {[...Array(2)].map((_, i) => (
            <Skeleton
              key={i}
              variant="rounded"
              height={loaderItemHeight}
              style={{ marginBottom: 10 }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default TableComponent;
